import { differenceInHours, isAfter, isSameDay } from 'date-fns';
import store from 'store';

import { LearnRecapState } from '../redux/reducers/learnRecap';

import { isFeatureEnabled } from './featureFlags';
import { STORAGE_KEYS } from './useStorage';

/**
 * Prompt the user to see the learn recap modal if they have not seen it before.
 * If they have seen it before, show the learn recap modal. If they have skipped
 * it twice, do nothing.
 */
export function showLearnRecap({
  courseId,
  isUserEnabledForLearnRecap,
  learnRecap,
  showLearnRecapModal,
  showShowOrSkipRecapModal,
  userEligibleForLearnRecapCallback,
}: {
  courseId?: number;
  isUserEnabledForLearnRecap: boolean;
  learnRecap: LearnRecapState;
  showLearnRecapModal: () => void;
  showShowOrSkipRecapModal: () => void;
  userEligibleForLearnRecapCallback: () => void;
}): void {
  if (courseId == null || learnRecap == null || !isUserEnabledForLearnRecap) {
    return;
  }

  const today = new Date();
  const learnRecapDate = learnRecap.lastActiveDate;
  const lastSeenDate = new Date(
    store.get(`${STORAGE_KEYS.LEARN_RECAP_LAST_SEEN}_${courseId}`) ?? 0,
  );

  const isLastSeenDateGreaterThanLearnRecapDate = isAfter(
    lastSeenDate,
    learnRecapDate,
  );

  const hasSeenLearnRecapToday = isSameDay(today, lastSeenDate);
  if (hasSeenLearnRecapToday || isLastSeenDateGreaterThanLearnRecapDate) {
    return;
  }

  const isLearnRecapFromToday = isSameDay(today, learnRecapDate);
  const elapsedHours = differenceInHours(today, learnRecapDate);

  // Early return if learn recap is from today or if it has been less than 6 hours
  if (isLearnRecapFromToday || elapsedHours < 6) {
    return;
  }

  // At this point, we know the user is eligible for the learn recap modal
  if (!isFeatureEnabled('cp--learn-recap')) {
    return;
  }

  userEligibleForLearnRecapCallback();

  const userHasEverSeenLearnRecap = store.get(
    STORAGE_KEYS.LEARN_RECAP_EVER_SEEN,
  );
  const userHasSkippedLearnRecapTwice =
    store.get(STORAGE_KEYS.LEARN_RECAP_SKIP_COUNT, 0) >= 2;

  // Update the last seen date to today
  if (!userHasSkippedLearnRecapTwice) {
    store.set(`${STORAGE_KEYS.LEARN_RECAP_LAST_SEEN}_${courseId}`, new Date());
  }

  if (!userHasEverSeenLearnRecap && !userHasSkippedLearnRecapTwice) {
    showShowOrSkipRecapModal();
  } else if (userHasEverSeenLearnRecap) {
    showLearnRecapModal();
  }
}
